/**
 * Pacific-time (America/Los_Angeles) display and wall-clock conversion for
 * treatment-plan date fields. Stored FHIR values remain UTC ISO strings; only
 * the provider-facing rendering/editing is pinned to PT.
 */

const PACIFIC_TIME_ZONE = 'America/Los_Angeles';

interface WallClockParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

const partsFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: PACIFIC_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
});

function pacificParts(instant: Date): WallClockParts {
  const parts: Record<string, string> = {};
  for (const part of partsFormatter.formatToParts(instant)) {
    parts[part.type] = part.value;
  }
  return {
    year: Number(parts.year),
    month: Number(parts.month),
    day: Number(parts.day),
    hour: Number(parts.hour),
    minute: Number(parts.minute),
    second: Number(parts.second),
  };
}

/** Milliseconds to add to a UTC instant to get its PT wall-clock fields (negative offset). */
function pacificOffsetMs(instant: Date): number {
  const p = pacificParts(instant);
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  return asUtc - instant.getTime();
}

/** "MM/DD/YYYY, h:mm AM/PM PT" for an ISO instant; returns the input when unparseable. */
export function formatPacificDateTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const formatted = date.toLocaleString('en-US', {
    timeZone: PACIFIC_TIME_ZONE,
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  return `${formatted} PT`;
}

/**
 * Convert an ISO instant into a Date whose *local* calendar fields equal the
 * PT wall clock — what a date picker should display for PT editing.
 */
export function isoToPacificWallClock(iso: string): Date | null {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  const p = pacificParts(date);
  return new Date(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
}

/**
 * Interpret a Date's local calendar fields as a PT wall-clock time and return
 * the corresponding UTC ISO instant. Inverse of {@link isoToPacificWallClock}.
 */
export function pacificWallClockToIso(wall: Date): string {
  const wallUtc = Date.UTC(
    wall.getFullYear(),
    wall.getMonth(),
    wall.getDate(),
    wall.getHours(),
    wall.getMinutes(),
    wall.getSeconds(),
  );
  // Two passes converge across DST transitions: the first guess may sit on the
  // wrong side of the boundary, the second uses the corrected instant's offset.
  let instant = wallUtc - pacificOffsetMs(new Date(wallUtc));
  instant = wallUtc - pacificOffsetMs(new Date(instant));
  return new Date(instant).toISOString();
}
